import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Hexagon, Loader2, FlaskConical, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const DEMO_EMAIL = "olga.chavoshi@gmail.com";
const DEMO_PASSWORD = "OneTechDemo2026!";

export default function Welcome() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const enter = async (withTour) => {
    setError("");
    setBusy(true);
    try {
      if (!user) await login(DEMO_EMAIL, DEMO_PASSWORD);
      if (withTour) localStorage.setItem("onet_tour_pending", "1");
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(apiError(err, "Unable to start the demo session"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="welcome-page">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="flex items-center gap-3 mb-14">
          <div className="w-11 h-11 rounded-sm bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.15)]">
            <Hexagon className="w-6 h-6 text-emerald-400" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-display font-bold text-xl text-zinc-100 tracking-tight">O.N.E.Tech</div>
            <div className="text-[10px] font-mono text-emerald-500 tracking-widest">OPERATIONAL INTELLIGENCE</div>
          </div>
        </div>

        <div className="max-w-2xl text-center">
          <div className="inline-block text-[10px] font-mono tracking-widest text-emerald-500 uppercase border border-emerald-500/30 bg-emerald-500/5 rounded-sm px-3 py-1.5 mb-8" data-testid="welcome-environment-badge">
            Simulated Demo Environment
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-100 leading-tight mb-6">
            Brokerage Operational Intelligence Infrastructure
          </h1>
          <p className="text-base md:text-lg text-zinc-400 leading-relaxed mb-12 max-w-xl mx-auto">
            O.N.E.Tech connects across a broker's existing CRM, trading platform, payment, KYC and
            communication systems to detect lifecycle leakage, decide the appropriate governed action,
            trigger multilingual communication or human escalation, and measure outcomes.
          </p>

          {error && (
            <div data-testid="welcome-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2 mb-6 max-w-md mx-auto">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="start-guided-tour-button"
              onClick={() => enter(true)}
              disabled={busy}
              className="inline-flex items-center gap-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-zinc-950 font-semibold rounded-sm px-10 py-3.5 text-sm tracking-wide transition-colors duration-200 shadow-[0_0_10px_rgba(16,185,129,0.2)] hover:shadow-[0_0_24px_rgba(16,185,129,0.45)]"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "PREPARING DEMO SESSION…" : "START GUIDED TOUR"}
              {!busy && <ArrowRight className="w-4 h-4" strokeWidth={2} />}
            </button>
            <button
              data-testid="explore-demo-button"
              onClick={() => enter(false)}
              disabled={busy}
              className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-60 text-zinc-200 font-semibold rounded-sm px-10 py-3.5 text-sm tracking-wide border border-zinc-700 hover:border-emerald-500/40 transition-colors duration-200"
            >
              EXPLORE DEMO
            </button>
          </div>
        </div>
      </div>

      <div className="pb-8 px-6 flex justify-center">
        <div className="flex items-start gap-2 text-[11px] text-zinc-500 max-w-lg" data-testid="synthetic-data-note">
          <FlaskConical className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500/70" strokeWidth={1.5} />
          <span className="font-mono tracking-wide">
            All clients, events and performance data shown are fictional and simulated for demonstration purposes.
          </span>
        </div>
      </div>
    </div>
  );
}
